














import { Component, Mixins } from "vue-property-decorator";
import { instanceOfIHisInformationSelectable } from "../../../../domain/document/items";
import { TextBoxItem } from "../../../../domain/document/items/TextBoxItem";
import { TextBoxInputMethodKinds } from "../../../../domain/document/inputMethodKinds";
import { TextBoxInputMethodOptionKinds } from "../../../../domain/document/inputMethodOptionKinds";
import {
  ForegroundDrawableObjectMixin,
  FontSizeAutoAdjustableMixin,
  TextFreeInputableMixin
} from "./mixins";
import HisInfoTextBoxObject from "./HisInfoTextBoxObject.vue";
import TextBoxObject from "./TextBoxObject.vue";
import { IFreeInputableObject } from "./IFreeInputableObject";
import { IItemCopyableObject } from "./IItemCopyableObject";
import { FontSizeCalculator } from "../../../../domain/drawing";
import { NumericInputFormatter } from "../../../../domain/document";

@Component({
  components: {
    FreeInputTextBoxItem: TextBoxObject,
    FixedTextBoxItem: TextBoxObject,
    HisInfoTextBoxItem: HisInfoTextBoxObject,
    NumberInputTextBoxItem: TextBoxObject,
    UserDefinedTextBoxItem: TextBoxObject,
  }
})
export default class TextBoxWrapperObject extends Mixins(
  ForegroundDrawableObjectMixin,
  FontSizeAutoAdjustableMixin,
  TextFreeInputableMixin
) implements IFreeInputableObject, IItemCopyableObject {
  editingSource: TextBoxItem;
  
  get freeInputText(): string {
    return this.editingSource.value;
  }

  set freeInputText(value: string) {
    const insertText = this.editingSource.isNumeric
      ? NumericInputFormatter.format(value, this.editingSource.numericFormat)
      : value;
    this.writeProperty((s: TextBoxItem) => {
      s.value = insertText;
      if (s.shouldAutoAdjustHeight) {
        s.geometry.rectangle.size.height = FontSizeCalculator.calculateObjectHeight(s);
      }
    });
  }

  get inputMethodKind(): TextBoxInputMethodKinds {
    return this.editingSource.inputMethodKind;
  }

  set inputMethodKind(value: TextBoxInputMethodKinds) {
    this.writeProperty((s: TextBoxItem) => {
      s.inputMethodKind = value;
      if (value !== TextBoxInputMethodKinds.MKSTemplate) {
        s.mksTemplateRegisteredId = null;
      }
      if (value !== TextBoxInputMethodKinds.DynamicTemplate) {
        s.dynamicTemplateRegisteredId = null;
      }
      if (value !== TextBoxInputMethodKinds.HisInfoSelect && instanceOfIHisInformationSelectable(s)) {
        s.setSelectedItems(null);
      }
    });
  }

  get inputMethodOptionKind(): TextBoxInputMethodOptionKinds {
    return this.editingSource.inputMethodOptionKind;
  }

  get isHisInfoSelectInputable(): boolean {
    return this.editingSource.isHisInfoSelectInputable;
  }

  setFocus(): void {
    // HIS項目をポップアップメニューからフリー入力に切り替えるとfocusが当たるが、そのまま色などを変更すると、
    // データ選択に戻ってしまうので対策している
    this.emitFocus();
  }
  
  /** テキストボックスの種別。 */
  get textBoxTypeKey(): string {
    if (this.editingSource.isHisInfo) { return "HisInfoTextBoxItem"; }
    if (this.editingSource.userDefinitionKey != null) { return "UserDefinedTextBoxItem"; }
    if (this.editingSource.isNumeric) { return "NumberInputTextBoxItem"; }
    if (this.editingSource.isFreeInputable) { return "FreeInputTextBoxItem"; }
    return "FixedTextBoxItem";
  }

  adjustTextFontSize(): void {
    if (!this.editingSource.isEmpty) {
      this.autoAdjustFontSize();
    }
  }

  updateInputElement(value: HTMLInputElement): void {
    this.inputableElement = value;
  }

  pasteText(value: string, isForcibly?: boolean): void {
    if (isForcibly) {
      this._pasteTextForcibly(value);
    } else {
      this._pasteText(value);
    }
  }

  overwriteText(value: string, isForcibly?: boolean): void {
    if (isForcibly) {
      this._overwriteTextForcibly(value);
    } else {
      this.overwriteText(value);
    }
  }

  private async _pasteText(value: string): Promise<void> {
    const insertText = this.editingSource.isNumeric
      ? NumericInputFormatter.format(value, this.editingSource.numericFormat)
      : value;
    this.insertTextToCaretPosition(insertText);
    await this.$nextTick();
    this.adjustTextFontSize();
  }

  private async _pasteTextForcibly(value: string): Promise<void> {
    if (!await this._setInputFree()) { return; }
    await this._pasteText(value);
  }

  private async _setInputFree(): Promise<boolean> {
    if (!this.isFreeInputable) {
      this.$logger.warn("フリー入力不可なので強制的に反映できません.");
      return false;
    }
    if (this.inputMethodKind !== TextBoxInputMethodKinds.FreeInput) {
      this.$logger.info("フリー入力じゃないので強制的にフリー入力に変更して反映します.");
      this.inputMethodKind = TextBoxInputMethodKinds.FreeInput;
      await this.$nextTick();
    }
    return true;
  }

  private async _overwriteText(value: string): Promise<void> {
    const insertText = this.editingSource.isNumeric
      ? NumericInputFormatter.format(value, this.editingSource.numericFormat)
      : value;
    this.freeInputText = insertText;
    await this.$nextTick();
    this.adjustTextFontSize();
  }

  private async _overwriteTextForcibly(value: string): Promise<void> {
    if (!await this._setInputFree()) { return; }
    await this._overwriteText(value);
  }

  get isSourceEmpty(): boolean {
    return !this.editingSource || this.editingSource.isEmpty;
  }
}
